// Shared quotation helpers used by admin and customer pages.
// These mirror the backend (index.js) exactly so what the admin sees while
// editing matches what gets saved and what the customer sees.

export const DEFAULT_TAX_RATE = 10; // backend default when Config "tax_rate" is not set
export const QUOTE_VALID_DAYS = 7;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

export const fmtSize = (b) => (!b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

/* ------------------------------------------------------------------
   Pricing — same formula as POST /api/requests/:id/quote:
   GST is charged on (parts + freight). Core charge is refundable and
   added after tax, untaxed.
------------------------------------------------------------------ */
export const partLine = (p, taxRate = DEFAULT_TAX_RATE) => {
  const qty = Number(p.quantity) || 1;
  const parts = qty * (Number(p.unitPrice) || 0);
  const freight = Number(p.freight) || 0;
  const core = Number(p.coreCharge) || 0;
  const tax = round2((parts + freight) * (taxRate / 100));
  return { parts: round2(parts), freight, core, tax, total: round2(parts + freight + tax + core) };
};

export const computeQuotePricing = (parts = [], taxRate = DEFAULT_TAX_RATE) => {
  const subtotal = parts.reduce((s, p) => s + (Number(p.quantity) || 1) * (Number(p.unitPrice) || 0), 0);
  const coreChargeTotal = parts.reduce((s, p) => s + (Number(p.coreCharge) || 0), 0);
  const freightTotal = parts.reduce((s, p) => s + (Number(p.freight) || 0), 0);
  const tax = round2((subtotal + freightTotal) * (taxRate / 100));
  const total = round2(subtotal + freightTotal + tax + coreChargeTotal);
  return { subtotal: round2(subtotal), coreChargeTotal: round2(coreChargeTotal), freightTotal: round2(freightTotal), taxRate, tax, total };
};

/* ------------------------------------------------------------------
   Status labels
------------------------------------------------------------------ */
// Request.status values the backend actually uses
export const REQUEST_STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-700",
  Quoted: "bg-blue-100 text-blue-700",
  Approved: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-600",
};
export const REQUEST_STATUS_DOT = {
  Pending: "bg-amber-500",
  Quoted: "bg-blue-500",
  Approved: "bg-green-500",
  Rejected: "bg-red-500",
};

// Request.quoteStatus / Quotation.status, as the ADMIN should read them
export const ADMIN_QUOTE_STATUS = {
  pending: { label: "No quote yet", cls: "bg-slate-100 text-slate-500" },
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  declined: { label: "Declined", cls: "bg-red-100 text-red-600" },
  revision_requested: { label: "Revision requested", cls: "bg-purple-100 text-purple-700" },
  overridden: { label: "Replaced", cls: "bg-slate-100 text-slate-400" },
};

export const quoteExpiry = (sentAt) => {
  if (!sentAt) return null;
  const days = Math.floor((Date.now() - new Date(sentAt)) / 86400000);
  return QUOTE_VALID_DAYS - days; // <= 0 means expired
};

/* ------------------------------------------------------------------
   Which quote may the CUSTOMER see?

   The backend returns drafts to customers, and when the admin saves a
   draft over a sent quote, the sent one is marked "overridden".
   request.quotedAt is set only when a quote is SENT, right after that
   quotation is created — so the newest quotation created at or before
   quotedAt is the last version the customer was actually sent.
------------------------------------------------------------------ */
export const pickCustomerQuote = (request, quotations = []) => {
  if (!request?.quotedAt) return null;
  const cutoff = new Date(request.quotedAt).getTime() + 1;
  return (
    [...quotations]
      .filter((q) => q.status !== "draft" && new Date(q.createdAt).getTime() <= cutoff)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null
  );
};

// Versions the customer may see in history (everything up to the last send)
export const customerVisibleVersions = (request, quotations = []) => {
  if (!request?.quotedAt) return [];
  const cutoff = new Date(request.quotedAt).getTime() + 1;
  return [...quotations]
    .filter((q) => q.status !== "draft" && new Date(q.createdAt).getTime() <= cutoff)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// One state the customer screens can switch on
// awaiting | sent | approved | declined | revision_requested | updating
export const customerQuoteState = (request) => {
  if (!request?.quotedAt) return "awaiting";
  switch (request.quoteStatus) {
    case "sent":
    case "approved":
    case "declined":
    case "revision_requested":
      return request.quoteStatus;
    case "draft":
      return "updating"; // admin is preparing a new version; show the last sent one
    default:
      return "awaiting";
  }
};

export const CUSTOMER_QUOTE_STATUS = {
  awaiting: { label: "Awaiting quote", cls: "bg-amber-50 text-amber-600" },
  sent: { label: "Quote ready", cls: "bg-blue-50 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-50 text-green-700" },
  declined: { label: "Declined", cls: "bg-red-50 text-red-600" },
  revision_requested: { label: "Revision requested", cls: "bg-purple-50 text-purple-700" },
  updating: { label: "Quote being updated", cls: "bg-slate-100 text-slate-600" },
};

/* ------------------------------------------------------------------
   Pagination — shows a sliding window instead of only pages 1–5
------------------------------------------------------------------ */
export const pageWindow = (page, totalPages, size = 5) => {
  const start = Math.max(1, Math.min(page - Math.floor(size / 2), totalPages - size + 1));
  const end = Math.min(totalPages, start + size - 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const downloadCsv = (filename, header, rows) => {
  // prefix cells that start with = + - @ so Excel doesn't run them as formulas
  const esc = (v) => {
    let t = String(v ?? "");
    if (/^[=+\-@]/.test(t) && !/^[-+]?\d+(\.\d+)?$/.test(t)) t = `'${t}`;
    return `"${t.replace(/"/g, '""')}"`;
  };
  const csv = [header.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/* ------------------------------------------------------------------
   "Needs attention" — computed by the backend (GET /requests → r.attention)
   Overdue = waiting longer than the 10-minute response promise.
------------------------------------------------------------------ */
export const RESPONSE_TARGET_MIN = 10;

export const ATTENTION = {
  revision_requested: { label: "Revision requested", short: "Revise quote" },
  awaiting_reply: { label: "Customer waiting for reply", short: "Reply needed" },
  needs_quote: { label: "Needs a quote", short: "Quote needed" },
  draft_not_sent: { label: "Quote draft not sent", short: "Send draft" },
};

export const minutesSince = (d) => (d ? Math.max(0, Math.round((Date.now() - new Date(d)) / 60000)) : 0);

export const waitedFor = (d) => {
  const m = minutesSince(d);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60 ? `${m % 60}m` : ""}`.trim();
  return `${Math.floor(h / 24)}d`;
};

export const isOverdue = (attention) => !!attention && minutesSince(attention.since) >= RESPONSE_TARGET_MIN;

// oldest waiting first
export const byUrgency = (a, b) => new Date(a.attention?.since || 0) - new Date(b.attention?.since || 0);

/* ------------------------------------------------------------------
   Quote versions: Quote 1, Quote 2, ...
   New quotes carry `version` from the server; older ones are numbered by
   creation order. Returns newest first, each with `_v`.
------------------------------------------------------------------ */
export const numberVersions = (quotes = [], { useStored = true } = {}) =>
  [...quotes]
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .map((q, i) => ({ ...q, _v: (useStored && q.version) || i + 1 }))
    .reverse();

// What an old version was before it got replaced (for labels like "Replaced · was declined")
export const replacedFrom = (q) =>
  q?.status === "overridden" && q.statusBeforeOverride && q.statusBeforeOverride !== "overridden" ? q.statusBeforeOverride : null;
